import { toast } from "react-toastify";
import { API_REST_URL } from "../../config/api";
import { AuthTokenService, IAuthTokenService } from "./auth-token-service";
import { BaseRestService, isServerError, TDataFormat } from "./base-rest-service";

export class SecuredRestService extends BaseRestService {
    private get authHeader() {
        return this.authTokenService.authHeader;
    }

    constructor(
        public baseUrl: string = API_REST_URL,
        private authTokenService: IAuthTokenService = new AuthTokenService(API_REST_URL),
    ) {
        super(baseUrl);
    }

    private manageSecureRequest = async <T>(request: () => T): Promise<T> => {
        try {
            return await request();
        } catch (e: any) {
            if (isServerError(e)) {
                // токен протух, происходит перезапрос
                if (e.status === 403) {
                    await this.authTokenService.updateCsrfToken();
                    return await request();
                }

                if (e.status === 404) {
                    throw e;
                }

                if (e.status === 401) {
                    throw e;
                }

                // 409 - код конфликта пори записи/обновлении данных, в этом случае показывается модальное окно
                if (e.status === 409) {
                    throw e;
                }

                // TODO: убрать показ тостов по дефолту, показывать вручную каждую ошибку?
                if (e.status === 422) {
                    throw e;
                }
            }

            // if (e?.name !== "AbortError") {
            //     toast.error(e?.message ? e.message : "Server error")
            // }

            throw e;
        }
    };

    async get<T>(args: {
        url: string;
        request?: Record<string, string>;
        responseOptions?: Record<string, string>,
        options?: RequestInit;
    }): Promise<T> {
        return this.manageSecureRequest((): Promise<T> => {
            const { options, request, url, responseOptions } = args;
            const headers = Object.assign(
                {},
                options?.headers,
                this.authHeader
            );
            return super.get({
                url,
                responseOptions,
                options: { ...options, headers },
                request,
            });
        });
    }

    async post<T>(args: {
        url: string;
        request?: Record<string, unknown>;
        dataFormat?: TDataFormat;
        file?: File;
        files?: File[];
        filesFieldName?: string;
        options?: RequestInit;
    }): Promise<T> {
        return this.manageSecureRequest((): Promise<T> => {
            const { options, request, url, file, dataFormat, files, filesFieldName } = args;
            const headers = Object.assign(
                {},
                options?.headers,
                this.authHeader
            );

            return super.post({
                url,
                options: { ...options, headers },
                request,
                file,
                files,
                filesFieldName,
                dataFormat
            });
        });
    }

    async put<T>(args: {
        url: string;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return this.manageSecureRequest((): Promise<T> => {
            const { options, request, url } = args;
            const headers = Object.assign(
                {},
                options?.headers,
                this.authHeader
            );
            return super.put({
                url,
                request,
                options: { ...options, headers },
            });
        });
    }

    async patch<T>(args: {
        url: string;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return this.manageSecureRequest((): Promise<T> => {
            const { options, request, url } = args;
            const headers = Object.assign(
                {},
                options?.headers,
                this.authHeader
            );
            return super.patch({
                url,
                request,
                options: { ...options, headers },
            });
        });
    }

    async delete<T>(args: {
        url: string;
        options?: RequestInit;
        request?: Record<string, string>;
    }): Promise<T> {
        return this.manageSecureRequest((): Promise<T> => {
            const { options, url } = args;
            const headers = Object.assign(
                {},
                options?.headers,
                this.authHeader
            );
            return super.delete({
                url,
                options: { ...options, headers },
                request: { ...args.request }
            });
        });
    }
}
